import { Component, inject, input, NgZone, OnDestroy, OnInit, output, ViewChild } from '@angular/core';
import { PhoneResource } from '../../../../shared/resources/phone.resource';
import { DeviceRegistrationStatus, PhoneStatus } from '../../../../shared/models/generated/ziroModels';
import { TranslateService } from '@ngx-translate/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ZiroIcons, ZiroStackedIcons } from '../../../../shared/models/ziro-icons.enum';
import { DeviceRegistrationStatusPollingService } from '../../../../shared/services/cisco/device-registration-status-polling.service';

@Component({
  selector: 'ziro-service-tile-edit',
  templateUrl: './service-tile-edit.component.html',
  styleUrls: ['./service-tile-edit.component.scss'],
  providers: [DeviceRegistrationStatusPollingService],
  standalone: false,
})
export class ZiroServiceTileEditComponent implements OnInit, OnDestroy {
  private phoneResource = inject(PhoneResource);
  private translateService = inject(TranslateService);
  protected ngZone = inject(NgZone);

  readonly isEnabled = input(true);
  readonly tooltip = input<string>();
  readonly icon = input<string>();
  readonly serviceName = input<string>();
  readonly description = input<string>();
  readonly isDeskPhone = input(false);
  readonly isDevice = input(false);
  readonly isStatusCheckInProgress = input(false);
  readonly hasMultipleDeskPhones = input(false);
  readonly deleteTooltip = input<string>();
  readonly deviceToolip = input<string>();
  readonly immediateCallForwardingTooltip = input<string>('');
  readonly deviceIconMarkup = input<string>();
  readonly isDeleteEnabled = input(true);
  readonly isDeleteShown = input(true);
  readonly isExchangeEnabled = input(true);
  readonly isImmediateCallForwardingEnabled = input<boolean>(false);
  readonly isExchangeShown = input(true);
  readonly isBeta = input(false);
  readonly phone = input<{
    serverId: number;
    name: string;
  }>();

  // Warning overlay
  readonly showWarning = input(false);
  readonly warningTooltip = input<string>('');

  readonly editTileClicked = output();
  readonly editDeskPhoneClicked = output();
  readonly deskPhoneAddClicked = output();
  readonly deskPhoneCopyClicked = output();
  readonly deleteClicked = output();
  readonly exchangeClicked = output();

  @ViewChild('tileTooltip') tileTooltip: NgbTooltip;

  ATTEMPTS_COUNT = 6;

  isHoverTile = false;
  isHoverAddDeskphone = false;
  isHoverCopyDeskphone = false;
  isHoverDelete = false;
  isHoverExchange = false;
  isHoverRefresh = false;
  isHoverImmediateCallForwarding = false;

  phoneStatus: DeviceRegistrationStatus;
  phoneStatusAttemptCount = 0;
  phoneStatusTimeout: number;
  phoneStatusInterval: number;
  phoneStatusAttemptTime: number;
  phoneStatusTooltip = '';
  ZiroStackedIcons = ZiroStackedIcons;
  ziroIcons = ZiroIcons;
  deviceRegistrationStatus = DeviceRegistrationStatus;

  ngOnInit(): void {
    if (this.phone()) {
      this.ngZone.runOutsideAngular(() => {
        this.getPhoneStatus();
      });
    }
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.phoneStatusTimeout);
    window.clearInterval(this.phoneStatusInterval);
  }

  onEditTileClicked(): void {
    this.editTileClicked.emit();
  }

  onDeskPhoneAddClicked(): void {
    this.deskPhoneAddClicked.emit();
  }

  onDeskPhoneCopyClicked(): void {
    this.deskPhoneCopyClicked.emit();
  }

  onDeleteClicked(): void {
    if (this.isDeleteEnabled()) {
      this.deleteClicked.emit();
    }
  }

  onExchangeClicked(): void {
    if (this.isExchangeEnabled()) {
      this.exchangeClicked.emit();
    }
  }

  getPhoneStatus(): void {
    window.clearTimeout(this.phoneStatusTimeout);
    const retryCyclesMs = [5000, 15000, 30000, 30000, 60000, 60000];
    this.phoneStatusTooltip = this.translateService.instant('tkey;userdetail.desk_phone.status.retry.tooltip');
    this.phoneResource.getPhoneStatus(this.phone().name, this.phone().serverId).subscribe((response: PhoneStatus) => {
      this.phoneStatusAttemptCount++;

      this.ngZone.run(() => (this.phoneStatus = response.status));

      if (
        (response.status === DeviceRegistrationStatus.UNREGISTERED ||
          response.status === DeviceRegistrationStatus.NOT_FOUND) &&
        this.phoneStatusAttemptCount <= 6
      ) {
        this.phoneStatusAttemptTime = Date.now() + retryCyclesMs[this.phoneStatusAttemptCount - 1];
        this.phoneStatusTimeout = window.setTimeout(
          () => {
            this.getPhoneStatus();
          },
          retryCyclesMs[this.phoneStatusAttemptCount - 1],
        );
      } else {
        this.phoneStatusTooltip =
          response.status === DeviceRegistrationStatus.NOT_FOUND ||
          response.status === DeviceRegistrationStatus.UNREGISTERED
            ? this.translateService.instant('tkey;userdetail.desk_phone.status.not.found.tooltip')
            : '';
      }
    });
  }

  getTooltip(): string {
    if (this.showWarning()) {
      return this.warningTooltip();
    }
    return this.phoneStatusTooltip || this.tooltip();
  }
}
